import { useEffect, useState } from 'react';
import { DismissRegular, SettingsRegular } from '@fluentui/react-icons';
import {
  makeStyles,
  Button,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Input,
  Tooltip,
  MessageBar,
  MessageBarActions,
  MessageBarTitle,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  fetchConfig,
  updateConfig,
} from '../services/fetchConfig';

const useStyles = makeStyles({
  section: {
    border: '1px solid #e1e1e1',
    borderRadius: '8px',
    padding: '10px 0',

    '&:not(:first-child)': {
      marginTop: '10px',
    }
  },
  formContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '30px',
    margin: '10px 60px'
  },
  formContainerThreeCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '30px',
    margin: '10px 60px'
  },
  formItem: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '4px',
  },
  numberInput: {
    width: '100px'
  },
  messageBar: {
    margin: '5px 0 0'
  }
});

const GrafanaDashboardConfig = () => {
  const styles = useStyles();
  const [initialOrangeAlertThreshold, setInitialOrangeAlertThreshold] = useState(null);
  const [initialRedAlertThreshold, setInitialRedAlertThreshold] = useState(null);
  const [orangeAlertThreshold, setOrangeAlertThreshold] = useState(null);
  const [redAlertThreshold, setRedAlertThreshold] = useState(null);
  const [initialMinIctrStart, setInitialMinIctrStart] = useState(null);
  const [minIctrStart, setMinIctrStart] = useState(null);
  const [initialEveryIctrIncrease, setInitialEveryIctrIncrease] = useState(null);
  const [everyIctrIncrease, setEveryIctrIncrease] = useState(null);
  const [initialMaxDiffBlockRecommendation, setInitialMaxDiffBlockRecommendation] = useState(null);
  const [maxDiffBlockRecommendation, setMaxDiffBlockRecommendation] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);

  const isButtonDisabled = (
    orangeAlertThreshold === initialOrangeAlertThreshold &&
      redAlertThreshold === initialRedAlertThreshold &&
      minIctrStart === initialMinIctrStart &&
      everyIctrIncrease === initialEveryIctrIncrease &&
      maxDiffBlockRecommendation === initialMaxDiffBlockRecommendation
  );

  useEffect(() => {
    const setState = async () => {
      try {
        const config = await fetchConfig();
        const orangeAlertThresholdValue = parseFloat(config.orange_alert_threshold);
        const redAlertThresholdValue = parseFloat(config.red_alert_threshold);
        const minIctrStartValue = parseInt(config.min_ictr_start);
        const everyIctrIncreaseValue = parseInt(config.every_ictr_increase);
        const maxDiffBlockRecommendationValue = parseInt(config.max_diff_block_recommendation);

        setInitialOrangeAlertThreshold(orangeAlertThresholdValue);
        setOrangeAlertThreshold(orangeAlertThresholdValue);
        setInitialRedAlertThreshold(redAlertThresholdValue);
        setRedAlertThreshold(redAlertThresholdValue);
        setInitialMinIctrStart(minIctrStartValue);
        setMinIctrStart(minIctrStartValue);
        setInitialEveryIctrIncrease(everyIctrIncreaseValue);
        setEveryIctrIncrease(everyIctrIncreaseValue);
        setInitialMaxDiffBlockRecommendation(maxDiffBlockRecommendationValue);
        setMaxDiffBlockRecommendation(maxDiffBlockRecommendationValue);
      } catch (error) {
        console.error('Error fetching live dashboard config:', error);
      }
    };

    setState();
  }, []);

  const handleOrangeAlertChange = (event) => {
    const newValue = parseFloat(event.target.value);
    if (!isNaN(newValue)) {
      setOrangeAlertThreshold(newValue);
    }
  };

  const handleRedAlertChange = (event) => {
    const newValue = parseFloat(event.target.value);
    if (!isNaN(newValue)) {
      setRedAlertThreshold(newValue);
    }
  };

  const handleMinIctrStartChange = (event) => {
    const newValue = parseInt(event.target.value);
    if (!isNaN(newValue)) {
      setMinIctrStart(newValue);
    }
  };

  const handleEveryIctrIncreaseChange = (event) => {
    const newValue = parseInt(event.target.value);
    if (!isNaN(newValue)) {
      setEveryIctrIncrease(newValue);
    }
  };

  const handleMaxDiffBlockRecommendationChange = (event) => {
    const newValue = parseInt(event.target.value);
    if (!isNaN(newValue)) {
      setMaxDiffBlockRecommendation(newValue);
    }
  };

  const handleDismissMessage = () => {
    setErrorMessage("");
    setShowMessage(false);
  };

  const renderMessage = () => {
    const messageContent = errorMessage || "Les changements ont bien été effectués!";
    return (
      <MessageBar
        className={styles.messageBar}
        intent={errorMessage ? "error" : "success"}
      >
        <MessageBarBody>
          <MessageBarTitle>{errorMessage ? "Erreur" : "Succès"}</MessageBarTitle>
          {messageContent}
        </MessageBarBody>
        <MessageBarActions
          containerAction={
            <Button
              aria-label="dismiss"
              appearance="transparent"
              icon={<DismissRegular/>}
              onClick={handleDismissMessage}
            />
          }
        />
      </MessageBar>
    );
  };

  const handleSubmit = async (event) => {
    if (redAlertThreshold < orangeAlertThreshold) {
      setErrorMessage("'Alerte Rouge' doit être supérieur ou égal à 'Alerte Orange'.");
      setShowMessage(true);
      setTimeout(handleDismissMessage, 5000);
      setOrangeAlertThreshold(initialOrangeAlertThreshold);
      setRedAlertThreshold(initialRedAlertThreshold);
      return;
    }

    if (minIctrStart < 500 || minIctrStart > 5000 || everyIctrIncrease < 500 || everyIctrIncrease > 5000) {
      setErrorMessage("Les paramètres de recommandations doivent être compris entre 500 et 5000.");
      setShowMessage(true);
      setTimeout(handleDismissMessage, 5000);
      setMinIctrStart(initialMinIctrStart);
      setEveryIctrIncrease(initialEveryIctrIncrease);
      return;
    }

    if (!Number.isInteger(maxDiffBlockRecommendation) || maxDiffBlockRecommendation < 0) {
      setErrorMessage("L'écart max. de blocage de recommandation doit être un entier supérieur ou égal à 0.");
      setShowMessage(true);
      setTimeout(handleDismissMessage, 5000);
      setMaxDiffBlockRecommendation(initialMaxDiffBlockRecommendation);
      return;
    }

    try {
      await updateConfig({
        orangeThreshold: orangeAlertThreshold,
        redThreshold: redAlertThreshold,
        minIctrStart: minIctrStart,
        everyIctrIncrease: everyIctrIncrease,
        maxDiffBlockRecommendation: maxDiffBlockRecommendation,
      });

      setInitialOrangeAlertThreshold(orangeAlertThreshold);
      setInitialRedAlertThreshold(redAlertThreshold);
      setInitialMinIctrStart(minIctrStart);
      setInitialEveryIctrIncrease(everyIctrIncrease);
      setInitialMaxDiffBlockRecommendation(maxDiffBlockRecommendation);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage("Une erreur est survenue lors des changements apportés à la configuration.");
    } finally {
      setShowMessage(true);
      setTimeout(handleDismissMessage, 3000);
    }
  }

  return (
    <Dialog>
      <DialogTrigger disableButtonEnhancement>
        <Tooltip content="Configuration du Rapport Temps Réel">
          <Button icon={<SettingsRegular />} />
        </Tooltip>
      </DialogTrigger>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            Configuration du Rapport Temps Réel
          </DialogTitle>
          <DialogContent>
            <div className={styles.section}>
              <div className={styles.formContainer}>
                <div className={styles.formItem}>
                  <label
                    htmlFor="orange-alert-threshold">Alerte Orange</label>
                  <Input
                    className={styles.numberInput}
                    id="orange-alert-threshold"
                    type="number"
                    step="0.5"
                    min="0"
                    max={redAlertThreshold}
                    value={orangeAlertThreshold}
                    onChange={handleOrangeAlertChange}
                  />
                </div>
                <div className={styles.formItem}>
                  <label
                    htmlFor="red-alert-threshold">Alerte Rouge</label>
                  <Input
                    className={styles.numberInput}
                    id="red-alert-threshold"
                    type="number"
                    step="0.5"
                    min={orangeAlertThreshold}
                    value={redAlertThreshold}
                    onChange={handleRedAlertChange}
                  />
                </div>
              </div>
            </div>
            <div className={styles.section}>
              <div className={styles.formContainerThreeCol}>
                <div className={styles.formItem}>
                  <label
                    htmlFor="min-ictr-start">Pains avant première recommandation</label>
                  <Input
                    className={styles.numberInput}
                    id="min-ictr-start"
                    type="number"
                    step="50"
                    min="500"
                    max="5000"
                    value={minIctrStart}
                    onChange={handleMinIctrStartChange}
                  />
                </div>
                <div className={styles.formItem}>
                  <label
                    htmlFor="every-ictr-increase">Pains entre recommandations</label>
                  <Input
                    className={styles.numberInput}
                    id="every-ictr-increase"
                    type="number"
                    step="50"
                    min="500"
                    max="5000"
                    value={everyIctrIncrease}
                    onChange={handleEveryIctrIncreaseChange}
                  />
                </div>
                <div className={styles.formItem}>
                  <label
                    htmlFor="max-diff-block-recommendation">Écart max. blocage recommandation</label>
                  <Input
                    className={styles.numberInput}
                    id="max-diff-block-recommendation"
                    type="number"
                    step="1"
                    min="0"
                    value={maxDiffBlockRecommendation}
                    onChange={handleMaxDiffBlockRecommendationChange}
                  />
                </div>
              </div>
            </div>
            {showMessage && renderMessage()}
          </DialogContent>
          <DialogActions>
              <Button
                appearance="primary"
                disabled={isButtonDisabled}
                onClick={handleSubmit}
              >Soumettre</Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Fermer</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default GrafanaDashboardConfig;
